// Tasks service - handles both API and demo mode based on feature settings

import type { Task, TaskFilters, TaskCreateDto, TaskUpdateDto, TaskStatistics } from '@/types/task';
import { getTasksDemo, getTaskDemo, getTaskCommentsDemo, getTaskAttachmentsDemo, getTaskActivityDemo, getTaskStatisticsDemo, createTaskDemo, updateTaskDemo, deleteTaskDemo, addLinkDemo, deleteLinkDemo } from './demo/tasks-demo-service';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getModuleMode } from '@/lib/feature-mode';
import { getApiUrl } from '@/lib/api-config';

// Helper function to append basic filter params - extracted to reduce cognitive complexity (S3776)
function appendBasicFilterParams(params: URLSearchParams, filters: TaskFilters): void {
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.type) params.append('type', filters.type);
  if (filters.priority) params.append('priority', filters.priority);
}

// Helper function to append array filter params - extracted to reduce cognitive complexity (S3776)
function appendArrayFilterParams(params: URLSearchParams, filters: TaskFilters): void {
  filters.assigneeIds?.forEach(id => params.append('assigneeIds', id));
  filters.tags?.forEach(tag => params.append('tags', tag));
}

// Helper function to append related entity filter params - extracted to reduce cognitive complexity (S3776)
function appendRelatedEntityParams(params: URLSearchParams, filters: TaskFilters): void {
  if (filters.assetId) params.append('assetId', filters.assetId);
  if (filters.locationId) params.append('locationId', filters.locationId);
  if (filters.auditId) params.append('auditId', filters.auditId);
  if (filters.exceptionId) params.append('exceptionId', filters.exceptionId);
}

// Helper function to append boolean and sorting filter params - extracted to reduce cognitive complexity (S3776)
function appendBooleanAndSortParams(params: URLSearchParams, filters: TaskFilters): void {
  if (filters.isOverdue) params.append('isOverdue', 'true');
  if (filters.dueToday) params.append('dueToday', 'true');
  if (filters.dueThisWeek) params.append('dueThisWeek', 'true');
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.order) params.append('order', filters.order);
}

/**
 * Get the API base URL for tasks
 */
function getApiBaseUrl(): string {
  return getApiUrl() ?? getBackendUrl();
}

/**
 * Fetch from API with authentication
 */
async function fetchAPI(endpoint: string, options?: RequestInit) {
  const apiBaseUrl = getApiBaseUrl();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  const authConfig = getAuthConfig();

  if (authConfig.getAccessToken) {
    try {
      const token = await authConfig.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting access token:', error);
    }
  }

  const instanceId = authConfig.instanceId ?? 'default-instance';
  headers['X-Instance-Id'] = instanceId;

  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = `API error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Check if we should use demo mode for tasks
 */
function shouldUseDemoMode(): boolean {
  const mode = getModuleMode('workItems');
  return mode === 'demo';
}

/**
 * Map backend WorkItemDto to frontend Task type
 */
function mapWorkItemToTask(workItem: any): Task {
  return {
    ...workItem,
    // Map backend field names to frontend field names
    createdAt: workItem.createdDate || workItem.createdAt,
    updatedAt: workItem.modifiedDate || workItem.updatedAt,
    createdBy: workItem.createdByUserName ?? workItem.createdBy ?? 'Unknown',
    updatedBy: workItem.modifiedByUserName || workItem.updatedBy,
    lastActivityAt: workItem.lastActivityAt || workItem.modifiedDate || workItem.createdDate,
    lastActivityBy: workItem.lastActivityByUserName || workItem.lastActivityBy,
    // Map assignee fields
    assignee: workItem.assignedToUserId ? {
      id: workItem.assignedToUserId,
      name: workItem.assignedToUserName ?? 'Unknown',
      email: workItem.assignedToUserEmail ?? '',
      avatar: workItem.assignedToUserAvatar,
      type: (workItem.assignedToUserType ?? 'user') as 'user' | 'group'
    } : undefined,
    // Ensure arrays exist
    links: workItem.links ?? [],
    linkedAssets: workItem.linkedAssets ?? [],
    tags: workItem.tags ?? [],
    checklists: workItem.checklists ?? [],
    subtasks: workItem.subtasks ?? [],
    // Ensure counts exist
    commentsCount: workItem.comments?.length ?? 0,
    attachmentsCount: workItem.attachments?.length ?? 0,
  };
}

/**
 * Build query parameters from task filters - refactored to reduce cognitive complexity (S3776)
 */
function buildTaskFilterParams(filters?: TaskFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (!filters) return params;

  appendBasicFilterParams(params, filters);
  appendArrayFilterParams(params, filters);
  appendRelatedEntityParams(params, filters);
  appendBooleanAndSortParams(params, filters);

  return params;
}

/**
 * Get tasks with filtering and pagination
 */
export async function getTasks(filters?: TaskFilters): Promise<{ data: Task[]; total: number; page: number; pageSize: number; totalPages: number }> {
  if (shouldUseDemoMode()) {
    return getTasksDemo(filters);
  }

  try {
    const params = buildTaskFilterParams(filters);
    const queryString = params.toString();
    const queryPart = queryString ? `?${queryString}` : '';
    const endpoint = `/api/WorkItems/filter${queryPart}`;
    
    const result = await fetchAPI(endpoint);
    
    // Map API response structure to frontend expected structure
    // API returns: { items: [...], totalCount: 46, page: 1, pageSize: 100, totalPages: 1 }
    // Frontend expects: { data: [...], total: 46, page: 1, pageSize: 100, totalPages: 1 }
    return {
      data: (result.items ?? []).map(mapWorkItemToTask),
      total: result.totalCount ?? 0,
      page: result.page ?? 1,
      pageSize: result.pageSize ?? 100,
      totalPages: result.totalPages ?? 1
    };
  } catch (error) {
    console.error('[TasksService] API call failed, falling back to demo data:', error);
    // Fallback to demo data if API fails
    return getTasksDemo(filters);
  }
}

/**
 * Get a single task by ID
 */
export async function getTask(id: string): Promise<Task> {
  if (shouldUseDemoMode()) {
    return getTaskDemo(id);
  }

  try {
    const result = await fetchAPI(`/api/WorkItems/${id}`);
    return mapWorkItemToTask(result);
  } catch (error) {
    console.error('[TasksService] API call failed, falling back to demo data:', error);
    // Fallback to demo data if API fails
    return getTaskDemo(id);
  }
}

/**
 * Create a new task
 */
export async function createTask(taskData: TaskCreateDto): Promise<Task> {
  if (shouldUseDemoMode()) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return createTaskDemo(taskData);
  }

  const result = await fetchAPI('/api/WorkItems', {
    method: 'POST',
    body: JSON.stringify(taskData),
  });
  return mapWorkItemToTask(result);
}

/**
 * Update an existing task
 */
export async function updateTask(id: string, taskData: TaskUpdateDto): Promise<Task> {
  if (shouldUseDemoMode()) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return updateTaskDemo(id, taskData);
  }

  const result = await fetchAPI(`/api/WorkItems/${id}`, {
    method: 'PUT',
    body: JSON.stringify(taskData),
  });
  return mapWorkItemToTask(result);
}

/**
 * Delete a task
 */
export async function deleteTask(id: string): Promise<void> {
  if (shouldUseDemoMode()) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    deleteTaskDemo(id);
    return;
  }

  await fetchAPI(`/api/WorkItems/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Normalize a comment object to ensure all required fields are present
 */
function normalizeComment(comment: any, taskId: string): any {
  return {
    id: comment.id || `comment-${Date.now()}`,
    taskId: comment.taskId || taskId,
    content: comment.comment ?? comment.content ?? '',
    createdAt: comment.createdDate || comment.createdAt || new Date().toISOString(),
        createdBy: comment.createdByUserId ?? comment.createdBy ?? 'unknown',
        createdByName: comment.createdByUserName ?? comment.createdByName ?? 'Unknown User',
    createdByAvatar: comment.createdByAvatar || undefined,
    updatedAt: comment.updatedDate || comment.updatedAt,
  };
}

/**
 * Get task comments
 */
export async function getTaskComments(taskId: string): Promise<any[]> {
  if (shouldUseDemoMode()) {
    const comments = getTaskCommentsDemo(taskId);
    return comments.map((c: any) => normalizeComment(c, taskId));
  }

  try {
    const result = await fetchAPI(`/api/WorkItems/${taskId}/comments`);
    // Normalize comments to ensure all required fields are present
    return (result ?? []).map((c: any) => normalizeComment(c, taskId));
  } catch (error) {
    // Silently fall back to demo data if endpoint doesn't exist
    const comments = getTaskCommentsDemo(taskId);
    return comments.map((c: any) => normalizeComment(c, taskId));
  }
}

/**
 * Get task attachments
 */
export async function getTaskAttachments(taskId: string): Promise<any[]> {
  if (shouldUseDemoMode()) {
    return getTaskAttachmentsDemo(taskId);
  }

  try {
    const result = await fetchAPI(`/api/WorkItems/${taskId}/attachments`);
    return result;
  } catch (error) {
    // Silently fall back to demo data if endpoint doesn't exist
    return getTaskAttachmentsDemo(taskId);
  }
}

/**
 * Normalize an activity/history object to ensure all required fields are present
 */
function normalizeActivity(activity: any, taskId: string): any {
  // Backend history format uses PascalCase field names:
  // - Action: the action performed (e.g., "Status Changed", "Assigned", etc.)
  // - FieldName: the field that was changed (e.g., "Status", "AssignedTo", etc.)
  // - ChangedDate: when the change occurred
  // - ChangedByUserId: ID of user who made the change
  // - ChangedByUserName: name of user who made the change
  
  // Build a description from Action and FieldName
  // Handle both PascalCase (backend) and camelCase (frontend)
  const action = activity.Action ?? activity.action ?? activity.description ?? 'made a change';
  const fieldName = activity.FieldName || activity.fieldName;
  let description = action;
  if (fieldName) {
    description = `${action} (${fieldName})`;
  }
  
  return {
    id: activity.Id || activity.id || `activity-${Date.now()}-${Math.random()}`,
    taskId: activity.WorkItemId || activity.workItemId || activity.taskId || taskId,
    type: activity.Action ?? activity.action ?? activity.changeType ?? activity.type ?? 'field-update',
    description: description,
    createdAt: activity.ChangedDate || activity.changedDate || activity.createdDate || activity.createdAt || new Date().toISOString(),
        createdBy: activity.ChangedByUserId ?? activity.changedByUserId ?? activity.createdByUserId ?? activity.createdBy ?? 'unknown',
        createdByName: activity.ChangedByUserName ?? activity.changedByUserName ?? activity.createdByUserName ?? activity.createdByName ?? 'Unknown User',
    oldValue: activity.OldValue || activity.oldValue,
    newValue: activity.NewValue || activity.newValue,
    metadata: activity.metadata,
  };
}

/**
 * Get task activity (history)
 */
export async function getTaskActivity(taskId: string): Promise<any[]> {
  if (shouldUseDemoMode()) {
    return getTaskActivityDemo(taskId);
  }

  try {
    // Backend uses /history endpoint, not /activity
    const result = await fetchAPI(`/api/WorkItems/${taskId}/history`);
    console.log('[TasksService] Raw activity from API:', result);
    // Normalize activity to ensure all required fields are present
    const normalized = (result ?? []).map((a: any) => normalizeActivity(a, taskId));
    console.log('[TasksService] Normalized activity:', normalized);
    return normalized;
  } catch (error) {
    // Silently fall back to demo data if endpoint doesn't exist (404)
    console.warn('[TasksService] Failed to fetch activity, falling back to demo data:', error);
    return getTaskActivityDemo(taskId);
  }
}

/**
 * Get task statistics
 */
export async function getTaskStatistics(): Promise<TaskStatistics> {
  if (shouldUseDemoMode()) {
    return getTaskStatisticsDemo();
  }

  try {
    const result = await fetchAPI('/api/WorkItems/statistics');
    return result;
  } catch (error) {
    console.error('[TasksService] API call failed, falling back to demo data:', error);
    return getTaskStatisticsDemo();
  }
}

// Bulk Action Types
export interface BulkActionResult {
  successCount: number;
  failureCount: number;
  failedIds: string[];
  errors: string[];
}

export interface BulkUpdateStatusDto {
  workItemIds: string[];
  status: string;
}

export interface BulkUpdatePriorityDto {
  workItemIds: string[];
  priority: string;
}

export interface BulkAssignDto {
  workItemIds: string[];
  assignedToUserId?: string;
  assignedToUserName?: string;
  assignedToUserEmail?: string;
  assignedToUserAvatar?: string;
  assignedToUserType?: string;
}

export interface BulkDeleteDto {
  workItemIds: string[];
}

/**
 * Bulk update status for multiple tasks
 */
export async function bulkUpdateStatus(dto: BulkUpdateStatusDto): Promise<BulkActionResult> {
  if (shouldUseDemoMode()) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return { successCount: dto.workItemIds.length, failureCount: 0, failedIds: [], errors: [] };
  }

  return fetchAPI('/api/WorkItems/bulk/status', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

// Checklist operations
export async function addChecklistItem(taskId: string, item: { title: string; mandatory?: boolean }): Promise<any> {
  if (shouldUseDemoMode()) {
    return { id: `check-${Date.now()}`, ...item, completed: false, createdAt: new Date().toISOString() };
  }
  return fetchAPI(`/api/WorkItems/${taskId}/checklists`, {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

export async function updateChecklistItem(taskId: string, checklistId: string, item: { title?: string; completed?: boolean }): Promise<any> {
  if (shouldUseDemoMode()) {
    return { id: checklistId, ...item };
  }
  return fetchAPI(`/api/WorkItems/${taskId}/checklists/${checklistId}`, {
    method: 'PUT',
    body: JSON.stringify(item),
  });
}

export async function deleteChecklistItem(taskId: string, checklistId: string): Promise<void> {
  if (shouldUseDemoMode()) {
    return;
  }
  await fetchAPI(`/api/WorkItems/${taskId}/checklists/${checklistId}`, {
    method: 'DELETE',
  });
}

/**
 * Bulk update priority for multiple tasks
 */
export async function bulkUpdatePriority(dto: BulkUpdatePriorityDto): Promise<BulkActionResult> {
  if (shouldUseDemoMode()) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return { successCount: dto.workItemIds.length, failureCount: 0, failedIds: [], errors: [] };
  }

  return fetchAPI('/api/WorkItems/bulk/priority', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

/**
 * Bulk assign multiple tasks to a user
 */
export async function bulkAssign(dto: BulkAssignDto): Promise<BulkActionResult> {
  if (shouldUseDemoMode()) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return { successCount: dto.workItemIds.length, failureCount: 0, failedIds: [], errors: [] };
  }

  return fetchAPI('/api/WorkItems/bulk/assign', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

/**
 * Bulk delete multiple tasks
 */
export async function bulkDeleteTasks(dto: BulkDeleteDto): Promise<BulkActionResult> {
  if (shouldUseDemoMode()) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return { successCount: dto.workItemIds.length, failureCount: 0, failedIds: [], errors: [] };
  }

  return fetchAPI('/api/WorkItems/bulk/delete', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

// Subtask operations
export async function addSubtask(taskId: string, subtask: { title: string; assigneeId?: string; assigneeName?: string; dueDate?: string }): Promise<any> {
  if (shouldUseDemoMode()) {
    return { id: `sub-${Date.now()}`, ...subtask, status: 'New' };
  }
  return fetchAPI(`/api/WorkItems/${taskId}/subtasks`, {
    method: 'POST',
    body: JSON.stringify(subtask),
  });
}

export async function updateSubtask(taskId: string, subtaskId: string, subtask: { title?: string; status?: string; assigneeId?: string; assigneeName?: string; dueDate?: string }): Promise<any> {
  if (shouldUseDemoMode()) {
    return { id: subtaskId, ...subtask };
  }
  return fetchAPI(`/api/WorkItems/${taskId}/subtasks/${subtaskId}`, {
    method: 'PUT',
    body: JSON.stringify(subtask),
  });
}

export async function deleteSubtask(taskId: string, subtaskId: string): Promise<void> {
  if (shouldUseDemoMode()) {
    return;
  }
  await fetchAPI(`/api/WorkItems/${taskId}/subtasks/${subtaskId}`, {
    method: 'DELETE',
  });
}

// Link operations (for linked assets)
export async function addLink(taskId: string, link: { linkType: string; linkedEntityId: string; linkedEntityName?: string; linkedEntityDescription?: string }): Promise<any> {
  if (shouldUseDemoMode()) {
    return addLinkDemo(taskId, link);
  }
  return fetchAPI(`/api/WorkItems/${taskId}/links`, {
    method: 'POST',
    body: JSON.stringify(link),
  });
}

export async function deleteLink(taskId: string, linkId: string): Promise<void> {
  if (shouldUseDemoMode()) {
    deleteLinkDemo(taskId, linkId);
    return;
  }
  await fetchAPI(`/api/WorkItems/${taskId}/links/${linkId}`, {
    method: 'DELETE',
  });
}

// Comment operations
export async function addComment(taskId: string, comment: string): Promise<any> {
  if (shouldUseDemoMode()) {
    return {
      id: `comment-${Date.now()}`,
      taskId,
      content: comment,
      createdAt: new Date().toISOString(),
      createdBy: 'user-1',
      createdByName: 'Current User',
    };
  }
  return fetchAPI(`/api/WorkItems/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
}

// Export as default object for easier imports
export const tasksService = {
  getTasks,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  getTaskComments,
  getTaskAttachments,
  getTaskActivity,
  getTaskStatistics,
  // Checklist operations
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  // Subtask operations
  addSubtask,
  updateSubtask,
  deleteSubtask,
  // Link operations
  addLink,
  deleteLink,
  // Comment operations
  addComment,
  bulkUpdateStatus,
  bulkUpdatePriority,
  bulkAssign,
  bulkDeleteTasks,
};
